import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';
import { createWebRTCController } from '../hooks/useWebRTC';
import { useWebSocketContext } from './WebSocketContext';
import { useAuth } from './AuthContext';
import { bindAudioUnlock, unlockAudio } from '../utils/ringtone';

const RING_TIMEOUT_MS = 45000;
const ICE_FAIL_TIMEOUT_MS = 15000;
const CONNECT_TIMEOUT_MS = 20000;

const CallContext = createContext({
  callState: 'idle',
  callType: null,
  conversationId: null,
  peer: null,
  localStream: null,
  remoteStream: null,
  muted: false,
  cameraOff: false,
  minimized: false,
  connectedAt: null,
  peerRinging: false,
  startCall: async () => {},
  acceptCall: async () => {},
  rejectCall: () => {},
  endCall: () => {},
  toggleMute: () => {},
  toggleCamera: () => {},
  switchCamera: async () => {},
  setMinimized: () => {},
});

const CALL_EVENT_TYPES = new Set([
  'call_incoming',
  'call_accepted',
  'call_ringing',
  'call_reject',
  'call_end',
  'call_busy',
  'call_unavailable',
  'ice_candidate',
]);

export function CallProvider({ children }) {
  const { sendMessage, subscribe } = useWebSocketContext();
  const { user } = useAuth();

  const [callState, setCallState] = useState('idle'); // idle | ringing | incoming | accepting | connected | reconnecting
  const [callType, setCallType] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [peer, setPeer] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  // WhatsApp-style: true once peer device is online and ringing
  const [peerRinging, setPeerRinging] = useState(false);

  const controllerRef = useRef(null);
  const pendingOfferRef = useRef(null);
  /** ICE candidates that arrived before the callee created a peer connection */
  const pendingRemoteCandidatesRef = useRef([]);
  const ringTimerRef = useRef(null);
  const iceFailTimerRef = useRef(null);
  /** Watchdog: media never connected after SDP exchange */
  const connectTimerRef = useRef(null);
  /** True once ICE/media actually connected (avoids stale-state races) */
  const connectedRef = useRef(false);
  const conversationIdRef = useRef(null);
  const callStateRef = useRef('idle');
  const tabLockRef = useRef(null);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  // Unlock Web Audio on first click/key so incoming ringtone can play
  useEffect(() => bindAudioUnlock(), []);

  const clearTimers = useCallback(() => {
    if (ringTimerRef.current) {
      clearTimeout(ringTimerRef.current);
      ringTimerRef.current = null;
    }
    if (iceFailTimerRef.current) {
      clearTimeout(iceFailTimerRef.current);
      iceFailTimerRef.current = null;
    }
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
  }, []);

  const resetCall = useCallback(() => {
    clearTimers();
    controllerRef.current?.cleanup();
    controllerRef.current = null;
    pendingOfferRef.current = null;
    pendingRemoteCandidatesRef.current = [];
    connectedRef.current = false;
    setCallState('idle');
    setCallType(null);
    setConversationId(null);
    setPeer(null);
    setLocalStream(null);
    setRemoteStream(null);
    setMuted(false);
    setCameraOff(false);
    setMinimized(false);
    setConnectedAt(null);
    setPeerRinging(false);
  }, [clearTimers]);

  const markConnected = useCallback(() => {
    if (iceFailTimerRef.current) {
      clearTimeout(iceFailTimerRef.current);
      iceFailTimerRef.current = null;
    }
    if (connectTimerRef.current) {
      clearTimeout(connectTimerRef.current);
      connectTimerRef.current = null;
    }
    if (callStateRef.current === 'idle') return;
    connectedRef.current = true;
    callStateRef.current = 'connected';
    setCallState('connected');
    setConnectedAt((prev) => prev || Date.now());
  }, []);

  const flushPendingRemoteCandidates = useCallback(async () => {
    const ctrl = controllerRef.current;
    if (!ctrl) return;
    const queued = pendingRemoteCandidatesRef.current.splice(0);
    for (const candidate of queued) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await ctrl.addIceCandidate(candidate);
      } catch (_) {
        /* ignore bad/stale candidates */
      }
    }
  }, []);

  const signal = useCallback(
    (type, data = {}) => {
      const cid = data.conversation_id || conversationIdRef.current;
      if (!cid && type !== 'ice_candidate') return false;
      return sendMessage({
        type,
        data: {
          ...data,
          conversation_id: cid,
        },
      });
    },
    [sendMessage]
  );

  // Fail the call if media never connects after the SDP handshake.
  const armConnectWatchdog = useCallback(() => {
    if (connectTimerRef.current) clearTimeout(connectTimerRef.current);
    connectTimerRef.current = setTimeout(() => {
      connectTimerRef.current = null;
      if (connectedRef.current) return;
      if (callStateRef.current === 'idle') return;
      toast.error('Could not connect call');
      signal('call_end', {
        conversation_id: conversationIdRef.current,
        reason: 'connect_timeout',
      });
      resetCall();
    }, CONNECT_TIMEOUT_MS);
  }, [resetCall, signal]);

  const ensureController = useCallback(() => {
    if (controllerRef.current) return controllerRef.current;
    controllerRef.current = createWebRTCController({
      sendSignal: (type, payload) => {
        signal(type, {
          conversation_id: conversationIdRef.current,
          ...payload,
        });
      },
      onRemoteStream: (stream) => {
        setRemoteStream(stream);
        // Media can arrive before connectionState flips — leave "Connecting…"
        if (
          callStateRef.current === 'accepting' ||
          callStateRef.current === 'ringing'
        ) {
          markConnected();
        }
      },
      onConnectionState: (state) => {
        if (state === 'connected' || state === 'completed') {
          markConnected();
        } else if (state === 'disconnected') {
          setCallState('reconnecting');
          callStateRef.current = 'reconnecting';
          if (!iceFailTimerRef.current) {
            iceFailTimerRef.current = setTimeout(() => {
              toast.error('Call connection lost');
              signal('call_end', {
                conversation_id: conversationIdRef.current,
                reason: 'ice_failed',
              });
              resetCall();
            }, ICE_FAIL_TIMEOUT_MS);
          }
        } else if (state === 'failed' || state === 'closed') {
          if (callStateRef.current !== 'idle') {
            signal('call_end', {
              conversation_id: conversationIdRef.current,
              reason: 'connection_failed',
            });
            resetCall();
          }
        }
      },
    });
    return controllerRef.current;
  }, [markConnected, resetCall, signal]);

  const startCall = useCallback(
    async (convId, type = 'voice', peerInfo = null) => {
      if (callStateRef.current !== 'idle') {
        toast.error('Already in a call');
        return;
      }
      if (!convId) return;

      try {
        setCallState('ringing');
        setCallType(type);
        setConversationId(convId);
        setPeer(peerInfo);
        setMinimized(false);
        setPeerRinging(false);

        const ctrl = ensureController();
        const { offerSdp, localStream: stream, effectiveType } = await ctrl.startOffer(type);
        setLocalStream(stream);
        setCallType(effectiveType);
        setCameraOff(effectiveType !== 'video');

        const ok = signal('call_initiate', {
          conversation_id: convId,
          call_type: effectiveType,
          offer_sdp: offerSdp,
        });
        if (!ok) {
          toast.error('Not connected — cannot start call');
          resetCall();
          return;
        }

        ringTimerRef.current = setTimeout(() => {
          if (callStateRef.current === 'ringing') {
            signal('call_cancel', { conversation_id: convId, reason: 'timeout' });
            toast('No answer');
            resetCall();
          }
        }, RING_TIMEOUT_MS);
      } catch (err) {
        console.error('startCall failed', err);
        toast.error('Microphone/camera permission required');
        resetCall();
      }
    },
    [ensureController, resetCall, signal]
  );

  const acceptCall = useCallback(async () => {
    if (callStateRef.current !== 'incoming') return;
    const offer = pendingOfferRef.current;
    const convId = conversationIdRef.current;
    if (!offer || !convId) return;

    try {
      clearTimers();
      connectedRef.current = false;
      // Instant UI feedback while getUserMedia / SDP / ICE run
      callStateRef.current = 'accepting';
      setCallState('accepting');
      setMinimized(false);
      // Bail out if ICE never completes (was hanging forever on Connecting…)
      armConnectWatchdog();

      await unlockAudio();
      const ctrl = ensureController();
      const type = callType || 'voice';
      const { answerSdp, localStream: stream, effectiveType } = await ctrl.acceptOffer(
        offer,
        type
      );
      setLocalStream(stream);
      setCallType(effectiveType);
      setCameraOff(effectiveType !== 'video');
      // Apply ICE that arrived while we were still on the incoming UI
      await flushPendingRemoteCandidates();
      signal('call_accept', {
        conversation_id: convId,
        answer_sdp: answerSdp,
      });
      // Stay on 'accepting' until ICE / remote track marks connected
      pendingOfferRef.current = null;
    } catch (err) {
      console.error('acceptCall failed', err);
      toast.error('Could not access microphone/camera');
      signal('call_reject', { conversation_id: convId, reason: 'media_error' });
      resetCall();
    }
  }, [
    armConnectWatchdog,
    callType,
    clearTimers,
    ensureController,
    flushPendingRemoteCandidates,
    resetCall,
    signal,
  ]);

  const rejectCall = useCallback(() => {
    const convId = conversationIdRef.current;
    const state = callStateRef.current;
    // Allow cancel while stuck on Connecting…
    if (state === 'accepting') {
      if (convId) {
        signal('call_end', { conversation_id: convId, reason: 'cancelled' });
      }
      resetCall();
      return;
    }
    if (convId) {
      signal('call_reject', { conversation_id: convId, reason: 'rejected' });
    }
    resetCall();
  }, [resetCall, signal]);

  const endCall = useCallback(() => {
    const convId = conversationIdRef.current;
    const state = callStateRef.current;
    if (convId) {
      if (state === 'ringing') {
        signal('call_cancel', { conversation_id: convId, reason: 'cancelled' });
      } else {
        signal('call_end', { conversation_id: convId, reason: 'ended' });
      }
    }
    resetCall();
  }, [resetCall, signal]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      controllerRef.current?.setMicEnabled(!next);
      return next;
    });
  }, []);

  const toggleCamera = useCallback(() => {
    setCameraOff((prev) => {
      const next = !prev;
      controllerRef.current?.setCameraEnabled(!next);
      return next;
    });
  }, []);

  const switchCamera = useCallback(async () => {
    const stream = await controllerRef.current?.switchCamera();
    if (stream) setLocalStream(stream);
  }, []);

  // Handle signaling events from WebSocket
  useEffect(() => {
    const unsubscribe = subscribe((payload) => {
      const type = payload?.type;
      if (!CALL_EVENT_TYPES.has(type)) return;
      const data = payload.data || {};

      if (type === 'call_incoming') {
        const state = callStateRef.current;
        // Duplicate delivery (or same ring) — never auto-busy-reject this
        if (
          state !== 'idle' &&
          data.conversation_id &&
          data.conversation_id === conversationIdRef.current
        ) {
          if (data.offer_sdp) pendingOfferRef.current = data.offer_sdp;
          return;
        }
        if (state !== 'idle') {
          // Truly in another call
          sendMessage({
            type: 'call_reject',
            data: {
              conversation_id: data.conversation_id,
              reason: 'busy',
            },
          });
          return;
        }
        pendingOfferRef.current = data.offer_sdp;
        // Sync refs immediately so a duplicate WS event in the same tick
        // does not look like "idle" and auto-reject as busy.
        conversationIdRef.current = data.conversation_id;
        callStateRef.current = 'incoming';
        setConversationId(data.conversation_id);
        setCallType(data.call_type === 'video' ? 'video' : 'voice');
        setPeer({
          id: data.caller_id,
          name: data.caller_name,
          avatar: data.caller_avatar,
        });
        setCallState('incoming');
        setMinimized(false);
        if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
        ringTimerRef.current = setTimeout(() => {
          if (callStateRef.current === 'incoming') {
            sendMessage({
              type: 'call_reject',
              data: {
                conversation_id: data.conversation_id,
                reason: 'timeout',
              },
            });
            resetCall();
          }
        }, RING_TIMEOUT_MS);
        return;
      }

      if (type === 'call_ringing') {
        if (callStateRef.current !== 'ringing') return;
        if (data.conversation_id !== conversationIdRef.current) return;
        setPeerRinging(true);
        return;
      }

      if (type === 'call_accepted') {
        if (callStateRef.current !== 'ringing') return;
        if (data.conversation_id !== conversationIdRef.current) return;
        clearTimers();
        const ctrl = controllerRef.current;
        if (!ctrl) {
          console.error('applyAnswer failed: missing peer connection');
          toast.error('Failed to connect call');
          endCall();
          return;
        }
        // Bail out if ICE never completes on the caller side either
        armConnectWatchdog();
        // Apply answer; show CallScreen as Connecting until ICE stamps connectedAt
        Promise.resolve()
          .then(() => ctrl.applyAnswer(data.answer_sdp))
          .then(() => {
            // Stay on 'accepting' until ICE / remote track marks connected so
            // the timer only starts once media is actually flowing.
            if (callStateRef.current === 'ringing') {
              callStateRef.current = 'accepting';
              setCallState('accepting');
            }
            // Do NOT set connectedAt here — onConnectionState owns the timer start
          })
          .catch((err) => {
            console.error('applyAnswer failed', err);
            toast.error('Failed to connect call');
            endCall();
          });
        return;
      }

      if (type === 'call_reject' || type === 'call_end') {
        if (
          data.conversation_id &&
          conversationIdRef.current &&
          data.conversation_id !== conversationIdRef.current
        ) {
          return;
        }
        if (callStateRef.current === 'idle') return;
        if (type === 'call_reject') {
          toast(data.reason === 'busy' ? 'User is busy' : 'Call declined');
        }
        resetCall();
        return;
      }

      if (type === 'call_busy' || type === 'call_unavailable') {
        toast(
          type === 'call_busy'
            ? 'User is busy'
            : 'User is unavailable'
        );
        resetCall();
        return;
      }

      if (type === 'ice_candidate') {
        if (data.conversation_id !== conversationIdRef.current) return;
        // Buffer candidates that arrive before accept creates the peer connection
        if (!controllerRef.current) {
          if (data.candidate) {
            pendingRemoteCandidatesRef.current.push(data.candidate);
          }
          return;
        }
        controllerRef.current.addIceCandidate(data.candidate);
      }
    });
    return unsubscribe;
  }, [subscribe, sendMessage, resetCall, clearTimers, endCall, armConnectWatchdog]);

  // End call on tab close
  useEffect(() => {
    const onUnload = () => {
      if (callStateRef.current === 'idle') return;
      const cid = conversationIdRef.current;
      if (!cid) return;
      try {
        // Best-effort: send via existing WS
        sendMessage({
          type: callStateRef.current === 'ringing' ? 'call_cancel' : 'call_end',
          data: { conversation_id: cid, reason: 'tab_closed' },
        });
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [sendMessage]);

  // Multi-tab lock via BroadcastChannel
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel('ticksync-call-lock');
    tabLockRef.current = channel;
    channel.onmessage = (event) => {
      if (event.data?.type === 'call_active' && callStateRef.current === 'idle') {
        // Another tab holds the call — no local action needed
      }
      if (
        event.data?.type === 'call_taken' &&
        (callStateRef.current === 'incoming' || callStateRef.current === 'accepting')
      ) {
        // Only clear a pending ring on another tab — not mid-accept on this tab
        if (callStateRef.current === 'incoming') {
          resetCall();
        }
      }
    };
    return () => {
      channel.close();
      tabLockRef.current = null;
    };
  }, [resetCall]);

  useEffect(() => {
    if (!tabLockRef.current) return;
    if (callState !== 'idle') {
      tabLockRef.current.postMessage({ type: 'call_active', userId: user?.id });
      // Only signal "taken" once THIS tab is in the call — never while merely
      // ringing outbound (that would wipe the callee's incoming UI on the
      // same origin when testing two accounts in one browser).
      if (callState === 'connected' || callState === 'accepting') {
        tabLockRef.current.postMessage({ type: 'call_taken' });
      }
    }
  }, [callState, user?.id]);

  const value = useMemo(
    () => ({
      callState,
      callType,
      conversationId,
      peer,
      localStream,
      remoteStream,
      muted,
      cameraOff,
      minimized,
      connectedAt,
      peerRinging,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleCamera,
      switchCamera,
      setMinimized,
    }),
    [
      callState,
      callType,
      conversationId,
      peer,
      localStream,
      remoteStream,
      muted,
      cameraOff,
      minimized,
      connectedAt,
      peerRinging,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      toggleMute,
      toggleCamera,
      switchCamera,
    ]
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall() {
  return useContext(CallContext);
}
